import { calculateWaterfall } from './waterfallCalculator';

/**
 * FY26 Revenue Waterfall Default Metrics
 * Based on the FY26 projection methodology:
 * - SQL to SQO: 43.4%
 * - SQO to Closed Won: 28.8%
 * - Avg Deal Size: $194K
 * - Monthly Growth: ~10%
 * - Q1 Target: $4.2M | Q2 Target: $5.8M
 */
const FY26_DEFAULT_METRICS = {
  startingArr: 10522000,       // $10.522M starting ARR (Dec 2025)
  arpa: 194000,                // $194K average revenue per account
  sqlToSqoRate: 0.434,         // 43.4% SQL → SQO
  sqoToCwRate: 0.288,          // 28.8% SQO → Closed Won
  pipelineCoverageRatio: 3.5,  // 3.5x pipeline coverage
  startingSqls: 55,            // Starting SQLs (Jan 2026 - adjusted for Q1 $4.2M target)
  monthlyGrowthRate: 0.10,     // ~10% monthly growth
  decemberSurge: 25,           // December year-end surge
  spreadMultiplier: 1.0        // Scenario spread
};

// Generate FY26 waterfall data using the calculator
export const FY26_WATERFALL_DATA = calculateWaterfall(FY26_DEFAULT_METRICS);

/**
 * Converts the waterfall data to forecast format
 * Filters out total rows and converts month format
 * Includes base, worst, and best scenarios for scenario analysis
 */
const baseScenario = FY26_WATERFALL_DATA.filter(row => !row.isTotal).map(row => ({
  month: convertMonthToCode(row.month),
  sqls: row.sqls,
  sqos: row.sqos,
  closedWon: row.closedWon,
  revenue: row.monthlyArr,
  cumulativeRevenue: row.cumulativeArr,
  pipeline: row.pipeline,
  isHistorical: false
}));

// Worst scenario: 75% of base
const worstScenario = FY26_WATERFALL_DATA.filter(row => !row.isTotal).map(row => ({
  month: convertMonthToCode(row.month),
  sqls: Math.round(row.sqls * 0.75),
  sqos: Math.round(row.sqos * 0.75),
  closedWon: Math.round(row.closedWon * 0.75),
  revenue: Math.round(row.monthlyArr * 0.75),
  cumulativeRevenue: Math.round(row.cumulativeArr * 0.75),
  pipeline: row.pipeline * 0.75,
  isHistorical: false
}));

// Best scenario: 125% of base
const bestScenario = FY26_WATERFALL_DATA.filter(row => !row.isTotal).map(row => ({
  month: convertMonthToCode(row.month),
  sqls: Math.round(row.sqls * 1.25),
  sqos: Math.round(row.sqos * 1.25),
  closedWon: Math.round(row.closedWon * 1.25),
  revenue: Math.round(row.monthlyArr * 1.25),
  cumulativeRevenue: Math.round(row.cumulativeArr * 1.25),
  pipeline: row.pipeline * 1.25,
  isHistorical: false
}));

export const FY26_FORECAST_DATA = {
  base: baseScenario,
  worst: worstScenario,
  best: bestScenario,
  currentRevenue: 10522000,
  targets: [
    { name: '$10M by EOY 2025', value: 10000000, date: '2025-12', baseline: 7400000 },
    { name: '$20M by H1 2026', value: 20000000, date: '2026-06', baseline: 10522000 },
    { name: '$40M by EOY 2026', value: 40000000, date: '2026-12', baseline: 20000000 }
  ]
};

/**
 * Convert month name to month code (e.g., "Nov 2025" → "2025-11")
 */
function convertMonthToCode(monthStr) {
  const monthNameToNum = {
    'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
    'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
  };

  const parts = monthStr.split(' ');
  if (parts.length === 2) {
    const [name, year] = parts;
    return `${year}-${monthNameToNum[name]}`;
  }
  return monthStr;
}
